/**
 * ChangelogView — Timeline of all index versions across environments.
 * Shows version history with status badges, document counts, and who promoted each version.
 */

import React, { useState, useEffect, useCallback } from 'react';

import {
  RefreshCw, Loader2, Bot, Database, Clock,
  CheckCircle, XCircle, Wrench, Eye,
  Filter
} from 'lucide-react';
import type { ChangelogEntry, ChangelogStatus } from './types';
import { API_BASE, authFetch, getAgentColor } from './constants';

interface ChangelogViewProps {
  selectedEnvironment: string;
}

const STATUS_CONFIG: Record<ChangelogStatus, { label: string; bg: string; text: string; icon: React.ReactNode }> = {
  building:   { label: 'Building',   bg: '#dbeafe', text: '#1e40af', icon: <Wrench size={12} /> },
  testing:    { label: 'Testing',    bg: '#d1fae5', text: '#065f46', icon: <Eye size={12} /> },
  active:     { label: 'Active',     bg: '#d1fae5', text: '#065f46', icon: <CheckCircle size={12} /> },
  superseded: { label: 'Superseded', bg: '#f1f2f4', text: '#6f747b', icon: <Clock size={12} /> },
  deleted:    { label: 'Deleted',    bg: '#fee2e2', text: '#991b1b', icon: <XCircle size={12} /> },
};

const ChangelogView: React.FC<ChangelogViewProps> = ({ selectedEnvironment }) => {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterAgent, setFilterAgent] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [availableAgents, setAvailableAgents] = useState<string[]>([]);

  const fetchChangelog = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/admin/indexing/changelog?environment=${selectedEnvironment}`;
      if (filterAgent) url += `&agentId=${filterAgent}`;
      if (filterStatus) url += `&status=${filterStatus}`;

      const response = await authFetch(url);
      const data = await response.json();
      if (data.success) {
        setEntries(data.entries || []);
        setAvailableAgents(data.agents || []);
      }
    } catch (err) {
      console.error('Error fetching changelog:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedEnvironment, filterAgent, filterStatus]);

  useEffect(() => {
    fetchChangelog();
  }, [fetchChangelog]);

  const formatDate = (d: string | null | undefined) => {
    if (!d) return '-';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Group by agent, sort entries newest-first within each group
  const grouped: Record<string, ChangelogEntry[]> = {};
  for (const entry of entries) {
    if (!grouped[entry.agentId]) grouped[entry.agentId] = [];
    grouped[entry.agentId].push(entry);
  }
  for (const agentId of Object.keys(grouped)) {
    grouped[agentId].sort((a, b) => {
      const dateA = new Date(a.promotedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.promotedAt || b.createdAt || 0).getTime();
      return dateB - dateA;
    });
  }

  // Sort agent groups by most recent activity
  const sortedAgentIds = Object.keys(grouped).sort((a, b) => {
    const latestA = Math.max(...grouped[a].map(e => new Date(e.promotedAt || e.createdAt || 0).getTime()));
    const latestB = Math.max(...grouped[b].map(e => new Date(e.promotedAt || e.createdAt || 0).getTime()));
    return latestB - latestA;
  });

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <Filter size={14} style={{ color: 'var(--idx-text-muted)' }} />
        <select
          value={filterAgent}
          onChange={e => setFilterAgent(e.target.value)}
          style={{
            padding: '4px 8px', borderRadius: 6, border: '1px solid var(--idx-border-strong)',
            fontSize: 12, background: 'var(--idx-bg-primary)', cursor: 'pointer',
          }}
        >
          <option value="">All Agents</option>
          {availableAgents.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            padding: '4px 8px', borderRadius: 6, border: '1px solid var(--idx-border-strong)',
            fontSize: 12, background: 'var(--idx-bg-primary)', cursor: 'pointer',
          }}
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <button
          onClick={fetchChangelog}
          disabled={loading}
          style={{
            padding: '4px 10px', borderRadius: 6, border: '1px solid var(--idx-border-strong)',
            cursor: 'pointer', background: 'var(--idx-bg-primary)', display: 'flex', alignItems: 'center',
            gap: 4, fontSize: 12,
          }}
        >
          <RefreshCw size={12} className={loading ? 'spinning' : ''} /> Refresh
        </button>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40, color: 'var(--idx-text-muted)' }}>
          <Loader2 size={24} className="spinning" />
        </div>
      )}

      {!loading && entries.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--idx-text-muted)' }}>
          <Database size={32} style={{ margin: '0 auto 12px', color: 'var(--idx-text-faint)' }} />
          <p style={{ fontWeight: 500, marginBottom: 4 }}>No index versions found</p>
          <p style={{ fontSize: 13 }}>
            Index versions will appear here after you approve and build indexes through the lifecycle workflow.
          </p>
        </div>
      )}

      {!loading && sortedAgentIds.map(agentId => {
        const agentEntries = grouped[agentId];
        const color = getAgentColor(agentId);
        const activeEntry = agentEntries.find(e => e.status === 'active');

        return (
          <div key={agentId} style={{
            marginBottom: 20, border: '1px solid var(--idx-border)', borderRadius: 8,
            borderLeft: `4px solid ${color}`, overflow: 'hidden',
          }}>
            {/* Agent header */}
            <div style={{
              padding: '10px 16px', background: 'var(--idx-bg-secondary)', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center',
              borderBottom: '1px solid var(--idx-border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Bot size={16} style={{ color }} />
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                  {agentEntries[0]?.agentDisplayName || agentId}
                </span>
                <span style={{ fontSize: 12, color: 'var(--idx-text-muted)' }}>
                  {agentEntries.length} version{agentEntries.length !== 1 ? 's' : ''}
                </span>
              </div>
              {activeEntry && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <CheckCircle size={14} style={{ color: '#10b981' }} />
                  <span style={{ color: '#065f46', fontWeight: 500 }}>Active:</span>
                  <code style={{ background: '#d1fae5', padding: '1px 6px', borderRadius: 3, fontSize: 11 }}>
                    {activeEntry.indexName}
                  </code>
                </div>
              )}
            </div>

            {/* Timeline entries */}
            <div style={{ padding: '8px 0' }}>
              {agentEntries.map((entry, idx) => {
                const statusCfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.building;
                const isActive = entry.status === 'active';

                return (
                  <div key={entry.indexName + idx} style={{
                    padding: '8px 16px', display: 'flex', alignItems: 'flex-start', gap: 12,
                    background: isActive ? '#f0fdf4' : 'var(--idx-bg-primary)',
                    borderBottom: idx < agentEntries.length - 1 ? '1px solid var(--idx-border)' : 'none',
                  }}>
                    {/* Timeline dot */}
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%', marginTop: 4, flexShrink: 0,
                      background: isActive ? '#10b981' : entry.status === 'deleted' ? '#ef4444' : '#cfd2d6',
                      border: `2px solid ${isActive ? '#059669' : 'var(--idx-border)'}`,
                    }} />

                    {/* Entry content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <code style={{
                          fontSize: 12, fontWeight: 500,
                          color: isActive ? '#065f46' : 'var(--idx-text-secondary)',
                        }}>
                          {entry.indexName}
                        </code>
                        <span style={{
                          padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                          background: statusCfg.bg, color: statusCfg.text,
                          display: 'flex', alignItems: 'center', gap: 3,
                        }}>
                          {statusCfg.icon} {statusCfg.label}
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--idx-text-muted)', flexWrap: 'wrap' }}>
                        <span>Created: {formatDate(entry.createdAt)}</span>
                        {entry.createdBy && <span>by {entry.createdBy}</span>}
                        {entry.promotedAt && (
                          <span style={{ color: '#059669' }}>
                            Promoted: {formatDate(entry.promotedAt)} by {entry.promotedBy}
                          </span>
                        )}
                      </div>

                      {(entry.documentsAdded > 0 || entry.documentsUpdated > 0 || entry.documentsRemoved > 0) && (
                        <div style={{ display: 'flex', gap: 12, fontSize: 11, marginTop: 2 }}>
                          {entry.documentsAdded > 0 && (
                            <span style={{ color: '#16a34a' }}>+{entry.documentsAdded} added</span>
                          )}
                          {entry.documentsUpdated > 0 && (
                            <span style={{ color: '#2563eb' }}>~{entry.documentsUpdated} updated</span>
                          )}
                          {entry.documentsRemoved > 0 && (
                            <span style={{ color: '#dc2626' }}>-{entry.documentsRemoved} removed</span>
                          )}
                          {entry.totalDocuments > 0 && (
                            <span style={{ color: 'var(--idx-text-muted)' }}>{entry.totalDocuments} total vectors</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ChangelogView;
